import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  AlertBannerProvider,
  Box,
  Button,
  ButtonIcon,
  Icon,
  InlineMessage,
  Loader,
  ModalIsland,
  PaginationCondensed,
  PaginationGroup,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import moment from 'moment';

import {
  downloadPreLaunchReport,
  getPreLaunchReport,
  getPreLaunchReportHistory,
  getPreLaunchStatus,
  launchFromPreLaunch,
  previewFromPreLaunch,
  resetPreLaunch,
  retriggerPreLaunchAnalysis,
  setPreLaunchCampaignId,
  triggerPreLaunchAnalysis,
} from '../../redux/prelaunch/action';
import { useInvertedLocalize } from '../../utils/localization';

import EmptyState from './components/EmptyState';
import LaunchConfirmModal from './components/LaunchConfirmModal';
import PageFooter from './components/PageFooter';
import PageHeader from './components/PageHeader';
import invertedMessages from './messages';
import PreLaunchDashboard from './PreLaunchDashboard';
import PreLaunchDetail from './PreLaunchDetail';

import './CampaignPreLaunch.css';

const POLL_INTERVAL_MS = 10000;

function CampaignPreLaunch() {
  const dispatch = useDispatch();
  const location = useLocation();
  const l = useInvertedLocalize(invertedMessages);

  const queryParams = new URLSearchParams(location.search);
  const campaignIdParam = queryParams.get('campaignId');

  const {
    campaignId,
    statusData,
    isTriggerLoading,
    triggerResult,
    triggerError,
    triggerConflict,
    featureDisabled,
    forbidden,
    isHistoryLoading,
    historyData,
    reportData,
    isReportLoading,
    isDownloadLoading,
    detailData,
  } = useSelector((state) => state.certifications.prelaunch);
  const { ecmDateFormat: globalDateFormat } = useSelector(
    (state) => state?.user?.headerMenu
  );

  const [detailInfo, setDetailInfo] = useState(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(25);
  const cachedTotalCount = useRef(0);
  const [launchModal, setLaunchModal] = useState({
    isOpen: false,
    blockerCount: 0,
    blockerDetails: [],
    isWarning: false,
    pendingAction: null, // 'preview' | 'launch'
  });
  const [previewConfirmOpen, setPreviewConfirmOpen] = useState(false);
  const [launchConfirmOpen, setLaunchConfirmOpen] = useState(false);
  const [retriggerConfirmOpen, setRetriggerConfirmOpen] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isLaunchLoading, setIsLaunchLoading] = useState(false);
  const [launchingAlert, setLaunchingAlert] = useState(false);
  const pollIntervalRef = useRef(null);

  const cId = campaignId || campaignIdParam;

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (cIdArg, rId) => {
      stopPolling();
      pollIntervalRef.current = setInterval(() => {
        dispatch(getPreLaunchStatus({ campaignId: cIdArg, reportId: rId }));
      }, POLL_INTERVAL_MS);
    },
    [dispatch, stopPolling]
  );

  useEffect(() => {
    if (campaignIdParam) {
      dispatch(setPreLaunchCampaignId(campaignIdParam));
      dispatch(getPreLaunchReportHistory(campaignIdParam));
    }

    return () => {
      stopPolling();
      dispatch(resetPreLaunch());
    };
  }, [campaignIdParam]); // eslint-disable-line

  useEffect(() => {
    if (isHistoryLoading || !historyData) return;
    if (historyData.length === 0) return;

    const latest = historyData[0];

    if (latest.status === 'COMPLETED') {
      dispatch(getPreLaunchReport({ reportId: latest.reportId }));
    } else if (latest.status === 'RUNNING') {
      dispatch(
        getPreLaunchStatus({ campaignId: cId, reportId: latest.reportId })
      );
      startPolling(cId, latest.reportId);
    } else if (latest.status === 'FAILED') {
      dispatch(
        getPreLaunchStatus({ campaignId: cId, reportId: latest.reportId })
      );
    }
  }, [isHistoryLoading, historyData]); // eslint-disable-line

  useEffect(() => {
    if (!statusData) return;

    if (statusData.status === 'RUNNING') {
      startPolling(cId, statusData.reportId);
    }

    if (statusData.status === 'COMPLETED') {
      stopPolling();
      dispatch(getPreLaunchReport({ reportId: statusData.reportId }));
      dispatch(getPreLaunchReportHistory(cId));
    }

    if (statusData.status === 'FAILED') {
      stopPolling();
    }
  }, [statusData?.status, statusData?.reportId]); // eslint-disable-line

  useEffect(() => {
    if (triggerResult?.reportId) startPolling(cId, triggerResult.reportId);
  }, [triggerResult?.reportId]); // eslint-disable-line

  // ── Action handlers ──────────────────────────────────────────────────────
  const handleTrigger = () => {
    if (cId) dispatch(triggerPreLaunchAnalysis(cId));
  };

  const handleDownload = () => {
    if (reportData?.reportId)
      dispatch(downloadPreLaunchReport(reportData.reportId));
  };

  // Parses blockersJson from reportData. Backend sends an array of blocker
  // objects: { code, status ('FAIL'|'WARNING'), message, description }.
  // Returns [] if missing or malformed.
  const getParsedBlockers = () => {
    if (!reportData) return [];

    try {
      return JSON.parse(reportData.blockersJson || '[]');
    } catch {
      return [];
    }
  };

  // Opens LaunchConfirmModal with blocker details.
  // isWarning=false → FAIL modal (Close only, cannot proceed).
  // isWarning=true  → WARNING modal (Continue + Close, pendingAction drives
  //                   which confirm modal opens after Continue).
  // b.message is the full human-readable text sent by the backend — no i18n.
  const showBlockerModal = (items, isWarning = false, pendingAction = null) => {
    const details = items.map((b) => ({
      code: b.code || b.id || '',
      description: b.message || b.description || '',
    }));

    setLaunchModal({
      isOpen: true,
      blockerCount: items.length,
      blockerDetails: details,
      isWarning,
      pendingAction,
    });
  };

  // Preview flow:
  // 1. FAIL blockers present  → show blocker modal (blocked, no proceed).
  // 2. WARNING blockers present → show warning modal with pendingAction='preview'.
  // 3. No blockers             → open preview confirm modal directly.
  const handlePreview = () => {
    const blockers = getParsedBlockers();
    const failedBlockers = blockers.filter((b) => b.status === 'FAIL');
    const warningBlockers = blockers.filter((b) => b.status === 'WARNING');

    if (failedBlockers.length > 0) {
      showBlockerModal(failedBlockers, false, null);

      return;
    }

    if (warningBlockers.length > 0) {
      showBlockerModal(warningBlockers, true, 'preview');

      return;
    }

    setPreviewConfirmOpen(true);
  };

  const handlePreviewConfirm = () => {
    setPreviewConfirmOpen(false);
    setActionError(null);
    setIsPreviewLoading(true);
    dispatch(previewFromPreLaunch(cId))
      .then((res) => {
        if (res?.success === false) {
          setActionError(res?.error?.message || 'Preview failed.');
          setIsPreviewLoading(false);

          return;
        }

        setIsPreviewLoading(false);
        setLaunchingAlert(true);
        setTimeout(() => {
          window.location = '/ECM/campaign/list';
        }, 50);
      })
      .catch(() => {
        setActionError('Preview failed. Please try again.');
        setIsPreviewLoading(false);
      });
  };

  const handlePreviewConfirmClose = () => {
    setPreviewConfirmOpen(false);
  };

  // Launch flow — same gate logic as handlePreview but pendingAction='launch'.
  const handleLaunch = () => {
    const blockers = getParsedBlockers();
    const failedBlockers = blockers.filter((b) => b.status === 'FAIL');
    const warningBlockers = blockers.filter((b) => b.status === 'WARNING');

    if (failedBlockers.length > 0) {
      showBlockerModal(failedBlockers, false, null);

      return;
    }

    if (warningBlockers.length > 0) {
      showBlockerModal(warningBlockers, true, 'launch');

      return;
    }

    setLaunchConfirmOpen(true);
  };

  const handleLaunchConfirm = () => {
    setLaunchConfirmOpen(false);
    setActionError(null);
    setIsLaunchLoading(true);
    dispatch(launchFromPreLaunch(cId))
      .then((res) => {
        if (res?.success === false) {
          setActionError(res?.error?.message || 'Launch failed.');
          setIsLaunchLoading(false);

          return;
        }

        setIsLaunchLoading(false);
        setLaunchingAlert(true);
        setTimeout(() => {
          window.location = '/ECM/campaign/list';
        }, 50);
      })
      .catch(() => {
        setActionError('Launch failed. Please try again.');
        setIsLaunchLoading(false);
      });
  };

  const handleLaunchConfirmClose = () => {
    setLaunchConfirmOpen(false);
  };

  const handleLaunchModalClose = () => {
    setLaunchModal({
      isOpen: false,
      blockerCount: 0,
      blockerDetails: [],
      isWarning: false,
      pendingAction: null,
    });
  };

  // Called when user clicks Continue on the WARNING modal.
  // Closes the warning modal then opens the appropriate confirm modal
  // based on pendingAction set when the warning modal was opened.
  const handleLaunchModalProceed = () => {
    const { pendingAction } = launchModal;

    handleLaunchModalClose();

    if (pendingAction === 'preview') setPreviewConfirmOpen(true);
    else if (pendingAction === 'launch') setLaunchConfirmOpen(true);
  };

  const handleClose = () => {
    window.location = '/ECM/campaign/list?status=11';
  };

  const handleOpenDetail = (metric, label) => {
    setDetailInfo({ metric, label });
    setDetailPage(1);
    cachedTotalCount.current = 0;
  };

  const handleCloseDetail = () => {
    setDetailInfo(null);
    setDetailPage(1);
    setDetailPageSize(25);
    cachedTotalCount.current = 0;
  };

  const PAGE_SIZE_OPTIONS = [5, 15, 20, 25, 50, 100].map((s) => ({
    label: String(s),
    value: String(s),
    isChecked: s === detailPageSize,
  }));

  const handleDetailPageSizeChange = (size) => {
    setDetailPageSize(size);
    setDetailPage(1);
    cachedTotalCount.current = 0;
  };

  const handleRetrigger = () => {
    setRetriggerConfirmOpen(true);
  };

  const handleRetriggerConfirm = () => {
    setRetriggerConfirmOpen(false);

    if (cId) {
      dispatch(retriggerPreLaunchAnalysis(cId));
      window.location = '/ECM/campaign/list?status=10';
    }
  };

  const handleRetriggerConfirmClose = () => {
    setRetriggerConfirmOpen(false);
  };

  const handleDetailPageChange = (p) => setDetailPage(p);

  const rawTotal = detailData?.total ?? -1;

  if (rawTotal >= 0) cachedTotalCount.current = rawTotal;
  const detailTotalCount = cachedTotalCount.current;
  const detailTotalPages =
    detailData?.totalPages ||
    (detailTotalCount > 0 ? Math.ceil(detailTotalCount / detailPageSize) : 0);

  const currentStatus = statusData?.status || reportData?.status || '';

  // ── No campaign ID ────────────────────────────────────────────────────────
  if (!cId) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-loading'>
          {l(
            'No campaign ID provided. Please navigate here from a campaign record.'
          )}
        </Box>
      </Box>
    );
  }

  // ── Feature disabled ──────────────────────────────────────────────────────
  if (featureDisabled) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-empty'>
          {l(
            'Pre-Launch Analysis is disabled. Enable it in EcmConfig (PRELAUNCH_ENABLED=true) to use this feature.'
          )}
        </Box>
      </Box>
    );
  }

  // ── Forbidden ─────────────────────────────────────────────────────────────
  if (forbidden) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-empty'>
          {l(
            'You do not have permission to run Pre-Launch Analysis. Required role: Campaign Admin.'
          )}
        </Box>
      </Box>
    );
  }

  // ── Initial loading ───────────────────────────────────────────────────────
  if (isHistoryLoading && (!historyData || historyData.length === 0)) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='CampaignPreLaunch-body CampaignPreLaunch-loading'>
          <Loader kind='dots' format='inline' size='medium' color='brand' />
          {l('Loading pre-launch analytics…')}
        </Box>
      </Box>
    );
  }

  // ── No runs yet — show Start Analysis ────────────────────────────────────
  if (!historyData || historyData.length === 0) {
    return (
      <Box className='CampaignPreLaunch'>
        <Box className='PageHeader'>
          <Box className='PageHeader-titleGroup'>
            <Typography
              kind='h2'
              desktop='h2-d'
              mobile='h2-d'
              className='PageHeader-title'>
              {l('Pre-Launch Analytics')}
            </Typography>
            <Box className='PageHeader-subtitle'>
              <Box tag='span' className='PageHeader-campaignName'>
                {`${l('Campaign')} `}
                {String(cId).replace(/[^a-zA-Z0-9_-]/g, '')}
              </Box>
            </Box>
          </Box>
          <ButtonIcon
            kind='ghost'
            size='medium'
            icon={
              <Icon
                kind='Close'
                size='smallMedium'
                color='--color-foreground-neutral-subtle'
              />
            }
            onClick={handleClose}
            ariaLabel={l('close')}
            className='PageHeader-closeBtn'
          />
        </Box>

        <EmptyState
          triggerError={triggerError}
          triggerConflict={triggerConflict}
          isTriggerLoading={isTriggerLoading}
          onTrigger={handleTrigger}
          onClose={handleClose}
        />
      </Box>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <AlertBannerProvider>
      <StackingAlertBanner
        colorTheme='Success'
        title={l('Campaign is being launched')}
        isColonVisible={false}
        isVisible={launchingAlert}
        onCancel={() => setLaunchingAlert(false)}
      />
      <Box className='CampaignPreLaunch'>
        <PageHeader
          reportData={reportData}
          currentStatus={currentStatus}
          globalDateFormat={globalDateFormat}
          onClose={handleClose}
        />

        {/* ── Notice banner — shown when report data is available ──────────────── */}
        {reportData && (
          <InlineMessage
            kind='background'
            colorTheme='warning'
            size='medium'
            leftIcon={<Icon kind='AlertWarningFilled' size='small' />}
            text={l(
              'These results reflect your data as of {timestamp}.\nPre-Launch Analytics does not guarantee final campaign scope — data changes between this analysis and launch may cause variances.\nRe-run analysis before launching to reflect the latest data quality insights and other metrics.',
              {
                timestamp: reportData.completedAt
                  ? moment
                      .utc(reportData.completedAt)
                      .local()
                      .format(
                        `${(
                          globalDateFormat || 'MMM DD, YYYY'
                        ).toUpperCase()} hh:mm:ss`
                      )
                  : l('the analysis run time'),
              }
            )}
            className='CampaignPreLaunch-notice'
          />
        )}

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <Box
          className={`CampaignPreLaunch-body${
            detailInfo ? ' CampaignPreLaunch-body--noScroll' : ''
          }`}>
          {detailInfo ? (
            <Box id='plDetailContainer' className='PreLaunchDetail'>
              <Box className='PreLaunchDetail-header'>
                <Button
                  type='button'
                  kind='outlined'
                  size='medium'
                  leftIcon={<Icon kind='ArrowLeft' size='xSmall' />}
                  onClick={handleCloseDetail}>
                  {l('Back to dashboard')}
                </Button>
                <Typography kind='h5' className='PreLaunchDetail-title'>
                  {detailInfo.label}
                </Typography>
              </Box>
              <PreLaunchDetail
                metric={detailInfo.metric}
                page={detailPage}
                pageSize={detailPageSize}
              />
            </Box>
          ) : (
            <PreLaunchDashboard onDrillDown={handleOpenDetail} />
          )}
        </Box>

        {/* ── Detail pagination — fixed above footer, only in detail view ── */}
        {detailInfo && (
          <Box className='CampaignPreLaunch-detailPagination'>
            <PaginationGroup
              pageCount={detailTotalPages}
              pageIndex={detailPage}
              setPageIndex={handleDetailPageChange}
            />
            <PaginationCondensed
              count={detailTotalCount}
              range={detailPageSize}
              setRange={handleDetailPageSizeChange}
              rangeOptions={PAGE_SIZE_OPTIONS}
              pageCount={detailTotalPages}
              pageIndex={detailPage}
              setPageIndex={handleDetailPageChange}
            />
          </Box>
        )}

        {actionError && (
          <InlineMessage
            kind='background'
            colorTheme='critical'
            size='medium'
            leftIcon={<Icon kind='AlertCriticalFilled' size='small' />}
            text={actionError}
            className='CampaignPreLaunch-actionError'
          />
        )}

        <PageFooter
          reportData={reportData}
          currentStatus={currentStatus}
          isDownloadLoading={isDownloadLoading}
          isTriggerLoading={isTriggerLoading}
          isPreviewLoading={isPreviewLoading}
          isLaunchLoading={isLaunchLoading}
          triggerError={triggerError}
          triggerConflict={triggerConflict}
          onDownload={handleDownload}
          onRetrigger={handleRetrigger}
          onPreview={handlePreview}
          onLaunch={handleLaunch}
          onClose={handleClose}
          showOnlyClose={currentStatus === 'RUNNING' || isReportLoading}
        />

        <LaunchConfirmModal
          isOpen={launchModal.isOpen}
          blockerCount={launchModal.blockerCount}
          blockerDetails={launchModal.blockerDetails}
          isWarning={launchModal.isWarning}
          onClose={handleLaunchModalClose}
          onProceed={handleLaunchModalProceed}
        />

        <ModalIsland
          kind='warning'
          isOpen={previewConfirmOpen}
          onClose={handlePreviewConfirmClose}
          alertTitle={l('Confirm')}
          alertSubtitle={l(
            'Are you sure you want to generate a preview for this campaign?\nClick Yes to proceed or click No to cancel.'
          )}
          primaryButton={
            <Button
              type='button'
              kind='filled'
              size='medium'
              onClick={handlePreviewConfirm}>
              {l('Yes')}
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handlePreviewConfirmClose}>
              {l('No')}
            </Button>
          }
        />

        <ModalIsland
          kind='warning'
          isOpen={launchConfirmOpen}
          onClose={handleLaunchConfirmClose}
          alertTitle={l('Confirm')}
          alertSubtitle={l(
            'Are you sure you want to launch this campaign?\nClick Yes to proceed or click No to cancel.'
          )}
          primaryButton={
            <Button
              type='button'
              kind='filled'
              size='medium'
              onClick={handleLaunchConfirm}>
              {l('Yes')}
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handleLaunchConfirmClose}>
              {l('No')}
            </Button>
          }
        />

        <ModalIsland
          kind='warning'
          isOpen={retriggerConfirmOpen}
          onClose={handleRetriggerConfirmClose}
          alertTitle={l('Confirm')}
          alertSubtitle={l(
            'Are you sure you want to re-trigger the Pre-Launch Analysis?\nClick Yes to proceed or click No to cancel.'
          )}
          primaryButton={
            <Button
              type='button'
              kind='filled'
              size='medium'
              onClick={handleRetriggerConfirm}>
              {l('Yes')}
            </Button>
          }
          secondaryButton={
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={handleRetriggerConfirmClose}>
              {l('No')}
            </Button>
          }
        />
      </Box>
    </AlertBannerProvider>
  );
}

export default CampaignPreLaunch;
